import React, { HTMLInputTypeAttribute, KeyboardEvent, useEffect, useState } from 'react';
import TextField, { TextFieldVariants } from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import { SxProps, Typography } from '@mui/material';
import { DYNAMIC_INPUT_TYPES_ENUM } from '@/src/types/common.interface';
import AppUtils from '@/src/utils/AppUtils';
import lightTheme from '@/src/styles/themes/lightTheme';

type FlexWrapValues =
  | 'nowrap'
  | 'wrap'
  | 'wrap-reverse'
  | 'inherit'
  | 'initial'
  | 'revert'
  | 'revert-layer'
  | 'unset';

export type ICustomSxProps<Theme extends object = {}> = SxProps<Theme> & {
  flexWrap?: FlexWrapValues;
};

interface IChipsInputProps {
  type: HTMLInputTypeAttribute;
  id: string;
  label: string;
  value: string | string[];
  onChange?: (data: string[]) => void;
  error: boolean;
  helperText: string;
  isRequired: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  errorValues?: string[];
  sx?: ICustomSxProps;
  isAutoCompleteOff?: boolean;
  placeholder?: string;
  variant?: TextFieldVariants;
  chipsSeparator?: string;
}

const ChipsInput: React.FC<IChipsInputProps> = (props: IChipsInputProps) => {
  const {
    type,
    value,
    error,
    helperText,
    onChange,
    placeholder,
    errorValues,
    disabled,
    sx,
    variant,
    size,
    isAutoCompleteOff,
    chipsSeparator
  } = props;

  const [chips, setChips] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');

  const handleAddChip = () => {
    if (inputValue.trim() !== '' && !chips.includes(inputValue)) {
      const formattedInput = inputValue
        .split(chipsSeparator ?? ',')
        ?.map((chip: string) => chip.trim()); // Split by semicolon and trim whitespace
      const uniqueValues = new Set([...formattedInput, ...chips]);
      const currentValue = Array.from(uniqueValues);
      setChips(currentValue);
      setInputValue('');
      if (onChange) {
        onChange(currentValue);
      }
    }
  };

  const handleDeleteChip = (chipToDelete: string) => {
    const newChips = chips.filter((chip) => chip !== chipToDelete);
    setChips(newChips);
    if (onChange) {
      onChange(newChips);
    }
  };

  useEffect(() => {
    if (!value && chips.length > 0) {
      setChips([]);
    } else if (value && Array.isArray(value)) {
      setChips(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) =>
    AppUtils.blockInvalidInputChar(
      type === 'number' ? DYNAMIC_INPUT_TYPES_ENUM.INTEGER : DYNAMIC_INPUT_TYPES_ENUM.TEXT,
      event
    );

  return (
    <FormControl fullWidth>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        columnGap={1}
        borderColor={lightTheme.palette.grey[300]}
      >
        <Box display="flex">
          <TextField
            type={type}
            variant={variant ?? 'outlined'}
            size={size}
            value={inputValue}
            placeholder={placeholder}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                handleAddChip();
              } else {
                handleInputKeyDown(e);
              }
            }}
            disabled={disabled}
            autoComplete={isAutoCompleteOff ? 'off' : 'on'}
          />
          <Box mb="auto" mt="auto">
            <IconButton
              onClick={handleAddChip}
              size={size}
              color="primary"
              aria-label="add"
              disabled={disabled}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          flexDirection="row"
          textAlign="center"
          sx={{ ...sx, overflowX: 'scroll' }}
          maxWidth="100%"
          maxHeight={50}
        >
          {chips?.length > 0 ? (
            chips?.map((chip: string) => (
              <Chip
                key={chip}
                label={chip}
                onDelete={() => handleDeleteChip(chip)}
                style={{ margin: '4px' }}
                color={errorValues?.includes(chip) ? 'error' : 'default'}
                disabled={disabled}
              />
            ))
          ) : (
            <Typography variant="body1" textAlign="center">
              No selection
            </Typography>
          )}
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ textAlign: 'left', width: '100%' }}>
            {helperText}
          </Typography>
        )}
      </Box>
    </FormControl>
  );
};

export default ChipsInput;
